import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useVerification } from '../context/VerificationContext';
import AutoVerificationModal from './AutoVerificationModal';

function estimateRemaining(progress, options) {
  const remaining = (progress?.total || 0) - (progress?.processed || 0);
  if (remaining <= 0 || !options) return null;
  let msPerRecord = 0;
  if (options.verifyIco || options.verifyAddress) msPerRecord += 400;
  if (options.verifySignal) msPerRecord += 1400;
  if (msPerRecord === 0) return null;
  const ms = remaining * msPerRecord;
  const s = Math.floor(ms / 1000);
  if (s < 60)   return `~${s} s`;
  if (s < 3600) return `~${Math.floor(s / 60)} min`;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `~${h} h ${m} min`;
}

export default function VerificationStatusBar({ currentUserId, isDarkMode }) {
  const { task, pauseVerification, resumeVerification, stopVerification } = useVerification();
  const [showModal, setShowModal] = useState(false);

  if (!task) return null;

  const progressPct = task.progress?.total
    ? Math.min(100, Math.round(((task.progress.processed || 0) / task.progress.total) * 100))
    : 0;

  const remaining = task.status === 'running' ? estimateRemaining(task.progress, task.options) : null;

  const statusColors = {
    running:   { bar: 'bg-green-500',  text: 'text-green-400',  label: 'Probíhá' },
    paused:    { bar: 'bg-yellow-400', text: 'text-yellow-400', label: 'Pozastaveno' },
    completed: { bar: 'bg-blue-500',   text: 'text-blue-400',   label: 'Dokončeno' },
  };
  const sc = statusColors[task.status] || statusColors.paused;

  const bar = (
    <>
      <div className="fixed bottom-0 left-0 right-0 z-[9999] bg-gray-900 border-t border-gray-700">
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center gap-3">
          {/* Stav + jméno */}
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 flex-1 min-w-0 hover:opacity-80 transition text-left"
            title="Otevřít detail ověření"
          >
            {task.status === 'running' && (
              <span className="flex-shrink-0 inline-block w-2.5 h-2.5 rounded-full bg-green-400 animate-pulse" />
            )}
            {task.status === 'paused' && (
              <span className="flex-shrink-0 inline-block w-2.5 h-2.5 rounded-full bg-yellow-400" />
            )}
            {task.status === 'completed' && (
              <span className="flex-shrink-0 text-blue-400 text-sm">✓</span>
            )}
            <span className={`text-xs font-semibold flex-shrink-0 ${sc.text}`}>{sc.label}</span>
            <span className="text-white text-xs font-medium truncate">{task.ownerName}</span>
            {task.currentCompany && task.status === 'running' && (
              <span className="text-gray-400 text-xs truncate hidden sm:block">
                — {task.currentCompany}
              </span>
            )}
          </button>

          {/* Progress */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="hidden sm:block w-32 bg-gray-700 rounded-full h-1.5">
              <div
                className={`${sc.bar} h-1.5 rounded-full transition-all duration-500`}
                style={{ width: `${progressPct}%` }}
              />
            </div>
            <span className="text-gray-300 text-xs whitespace-nowrap">
              {(task.progress?.processed || 0).toLocaleString('cs-CZ')}
              {task.progress?.total ? ` / ${task.progress.total.toLocaleString('cs-CZ')}` : ''}
              {progressPct > 0 ? ` (${progressPct} %)` : ''}
            </span>
            {remaining && (
              <span className="text-gray-400 text-xs whitespace-nowrap hidden md:block">
                zbývá {remaining}
              </span>
            )}
          </div>

          {/* Tlačítka */}
          <div className="flex items-center gap-1 flex-shrink-0">
            {task.status === 'running' && (
              <button
                onClick={pauseVerification}
                title="Pozastavit"
                className="px-2 py-1 bg-yellow-600 hover:bg-yellow-700 text-white rounded text-xs font-medium"
              >
                ⏸
              </button>
            )}
            {task.status === 'paused' && (
              <button
                onClick={resumeVerification}
                title="Pokračovat"
                className="px-2 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-xs font-medium"
              >
                ▶
              </button>
            )}
            {task.status !== 'completed' && (
              <button
                onClick={stopVerification}
                title="Zastavit ověření"
                className="px-2 py-1 bg-red-700 hover:bg-red-800 text-white rounded text-xs font-medium"
              >
                ⏹
              </button>
            )}
            {task.status === 'completed' && (
              <button
                onClick={stopVerification}
                title="Zavřít"
                className="px-2 py-1 bg-gray-600 hover:bg-gray-700 text-white rounded text-xs font-medium"
              >
                ✕
              </button>
            )}
            <button
              onClick={() => setShowModal(true)}
              title="Detail"
              className="px-2 py-1 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded text-xs font-medium"
            >
              ↗
            </button>
          </div>
        </div>
      </div>

      {showModal && (
        <AutoVerificationModal
          onClose={() => setShowModal(false)}
          currentUserId={currentUserId}
          isDarkMode={isDarkMode}
        />
      )}
    </>
  );

  return ReactDOM.createPortal(bar, document.body);
}
